import React from "react";
import { Box, Paper, Typography, Stack, Switch } from "@mui/material";
import EditableField from "./EditableField";
import { formatBusinessType } from "./utils";
import { organizationIdOf, organizationNameOf, planOf } from "../../domain/account/organizationDetails";

interface OrganizationDetailsPanelProps {
  user: {
    userType?: string;
    schoolName?: string;
    orgId?: string;
    schoolId?: string;
    schoolDescription?: string;
    schoolType?: string;
    phoneNumber?: string | number;
    /** When true, API omits org phone from mobile app payloads (dashboard still shows it here). */
    hidePhoneInApp?: boolean;
    website?: string;
    PK?: string;
    tier?: string;
    sponsored?: boolean;
  };
  canEdit: boolean;
  isUpdatingName: boolean;
  isUpdatingDescription: boolean;
  isUpdatingPhoneNumber: boolean;
  isUpdatingWebsite: boolean;
  isUpdatingHidePhoneInApp?: boolean;
  onSaveName: (value: string) => Promise<void>;
  onSaveDescription: (value: string) => Promise<void>;
  onSavePhoneNumber: (value: string) => Promise<void>;
  onSaveWebsite: (value: string) => Promise<void>;
  onHidePhoneInAppChange?: (hide: boolean) => void | Promise<void>;
}

const OrganizationDetailsPanel: React.FC<OrganizationDetailsPanelProps> = ({
  user,
  canEdit,
  isUpdatingName,
  isUpdatingDescription,
  isUpdatingPhoneNumber,
  isUpdatingWebsite,
  isUpdatingHidePhoneInApp = false,
  onSaveName,
  onSaveDescription,
  onSavePhoneNumber,
  onSaveWebsite,
  onHidePhoneInAppChange,
}) => {
  const isTeam = user.userType === "team";
  const schoolId = organizationIdOf(user);
  const tierInfo = planOf(user.tier);
  const isSponsored = user.sponsored === true;

  return (
    <Box sx={{ order: { xs: 2, md: 1 } }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 3 },
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          boxShadow: "none",
          height: "100%",
        }}
      >
        <Typography
          variant="h6"
          fontWeight={600}
          color="text.primary"
          mb={3}
        >
          {isTeam ? "Organization Details" : "Circle Details"}
        </Typography>
        <Stack spacing={3}>
          {/* Circle/Organization Name */}
          <EditableField
            label={isTeam ? "Organization" : "Circle Name"}
            value={organizationNameOf(user)}
            canEdit={canEdit}
            isUpdating={isUpdatingName}
            onSave={onSaveName}
            testIdPrefix="name"
            variant="body1"
          />

          {/* Plan; Ask Noticeboard links here */}
          <Box id="plan">
            <Typography variant="caption" color="text.secondary" mb={0.5}>
              Plan
            </Typography>
            <Typography variant="body2" fontWeight={600}>
              {tierInfo.name}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: "0.75rem", lineHeight: 1.5, display: "block", mt: 0.5 }}
            >
              {tierInfo.description}
            </Typography>
          </Box>

          {/* Sponsored */}
          <Box>
            <Typography variant="caption" color="text.secondary" mb={0.5}>
              Billing
            </Typography>
            <Typography variant="body2" fontWeight={500}>
              {isSponsored ? "Sponsored (not paying)" : "Not sponsored"}
            </Typography>
          </Box>

          {/* Description */}
          <EditableField
            label="Description"
            value={user.schoolDescription || ""}
            emptyText="No description set"
            placeholder="Enter description"
            canEdit={canEdit}
            isUpdating={isUpdatingDescription}
            onSave={onSaveDescription}
            testIdPrefix="description"
          />

          {/* Type (read-only) */}
          <Box>
            <Typography variant="caption" color="text.secondary" mb={0.5}>
              Type
            </Typography>
            <Typography variant="body2" fontWeight={500}>
              {user.schoolType && user.schoolType.trim() !== ""
                ? formatBusinessType(user.schoolType)
                : "No type set"}
            </Typography>
          </Box>

          {/* Phone Number */}
          <EditableField
            label="Phone Number"
            value={
              typeof user.phoneNumber === "string" ? user.phoneNumber : ""
            }
            emptyText="No phone number set"
            placeholder="Enter phone number"
            canEdit={canEdit}
            isUpdating={isUpdatingPhoneNumber}
            onSave={onSavePhoneNumber}
            testIdPrefix="phone"
          />

          {onHidePhoneInAppChange && (
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 2,
                mt: -1,
              }}
            >
              <Switch
                checked={user.hidePhoneInApp === true}
                onChange={(_, checked) => void onHidePhoneInAppChange(checked)}
                disabled={!canEdit || isUpdatingHidePhoneInApp}
                color="primary"
                inputProps={{
                  "aria-label": "Hide phone number from mobile app",
                }}
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" fontWeight={500} color="text.primary">
                  Hide phone number from mobile app
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  When enabled, your organization phone is not included in data
                  sent to the Noticeboard mobile app. You can still view and edit
                  it here.
                </Typography>
              </Box>
            </Box>
          )}

          {/* Website */}
          <EditableField
            label="Website"
            value={
              typeof user.website === "string" ? user.website : ""
            }
            emptyText="No website set"
            placeholder="Enter website URL"
            canEdit={canEdit}
            isUpdating={isUpdatingWebsite}
            onSave={onSaveWebsite}
            testIdPrefix="website"
          />

          {/* Organization ID (OID) */}
          <Box>
            <Typography variant="caption" color="text.secondary" mb={0.5}>
              Organization ID (OID)
            </Typography>
            <Typography variant="body2" fontWeight={500}>
              {schoolId}
            </Typography>
          </Box>
        </Stack>
      </Paper>
    </Box>
  );
};

export default OrganizationDetailsPanel;
